import React, { memo, useMemo, useCallback, useState, Dispatch, SetStateAction } from "react";
import { useParams, useHistory } from "react-router-dom";
import { SettingsPageContainer } from "../../../../../components/Settings/SettingsPageContainer"
import { PasswordContainer } from "../../../../../components/PasswordContainer"
import { useLocalization } from "../../../../../hooks/localization"
import { SettingCheckBoxComponent } from "../../../../../components/Settings/SettingCheckboxComponent";
import { SettingInputComponent } from "../../../../../components/Settings/SettingInputComponent";
import { SettingSelectComponent } from "../../../../../components/Settings/SettingSelectComponent";
import { SettingColorInputComponent } from "../../../../../components/Settings/SettingColorInputComponent"
import { RgbColor } from "../../../../../components/ColorPicker/types";
import { useMapSelector } from '../../../../../hooks/map-selector';
import { SelectItem } from '../../../../../components/Select';
import { useSelector } from 'react-redux';
import { useRPC } from "../../../../../hooks/rpc";
import { useMessage } from "../../../../../hooks/message";
import { BridgeDeviceModel } from "../../../../../types/bridge-device";
import './style.scss';

import {
  BRIDGE_DEVICE_TYPE_ID_STP,
  BRIDGE_DEVICE_TYPE_ID_STPM,
  BRIDGE_DEVICE_TYPE_ID_STPM_F,
  BRIDGE_DEVICE_TYPE_ID_ST,
  BRIDGE_DEVICE_TYPE_ID_STS1,
  BRIDGE_DEVICE_TYPE_ID_STS2,
  BRIDGE_DEVICE_TYPE_ID_STS4,
  BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
  BRIDGE_DEVICE_TYPE_ID_KEYPAD
} from "../../../../../types/control";
import { useQuestion } from "../../../../../hooks/question";
import { useKeyboard } from "../../../../../hooks/keyboard";

const SUPPORTED_DEVICE_IDS: string[] = [
  BRIDGE_DEVICE_TYPE_ID_STP,
  BRIDGE_DEVICE_TYPE_ID_STPM,
  BRIDGE_DEVICE_TYPE_ID_STPM_F,
  BRIDGE_DEVICE_TYPE_ID_ST,
  BRIDGE_DEVICE_TYPE_ID_ST,
  BRIDGE_DEVICE_TYPE_ID_STS1,
  BRIDGE_DEVICE_TYPE_ID_STS2,
  BRIDGE_DEVICE_TYPE_ID_STS4,
  BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
  BRIDGE_DEVICE_TYPE_ID_KEYPAD
]

const ACTIVE_BUTTON_SUPPORTED_DEVICE_IDS: string[] = [
  BRIDGE_DEVICE_TYPE_ID_STS1,
  BRIDGE_DEVICE_TYPE_ID_STS2,
  BRIDGE_DEVICE_TYPE_ID_STS4,
  BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
]

enum ModeOption {
  NONE = 'none',
  NEW = 'new',
  EXISTING = '0',
  WARNING = '1',
  FLAME = '2',
  AMBIENT = '3'
}

type ButtonSettingsProps = {
  title: string,
  hasColor: boolean,
  setHasColor: Dispatch<SetStateAction<boolean>>,
  mode?: ModeOption,
  setMode?: Dispatch<SetStateAction<string>>,
  color?: RgbColor,
  setColor: Dispatch<SetStateAction<RgbColor>>,
  selectedDeviceList?: Array<SelectItem<string>>,
  setSelectedDeviceList?: Dispatch<SetStateAction<Array<SelectItem<string>>>>,
  deviceOptionList: Array<SelectItem<string>>
}

const BACK_URL = '/settings/custom-menu/lucent-themes/';

const ButtonSettings = memo((props: ButtonSettingsProps) => {
  const {
    title,
    hasColor, setHasColor,
    mode, setMode,
    color, setColor,
    selectedDeviceList, setSelectedDeviceList,
    deviceOptionList,
  } = props;

  const [rpc] = useRPC();
  const { t } = useLocalization();
  const { success, failure } = useMessage();

  const isActiveButton = useMemo(() => {
    // If setMode is null, then we are showing active button color 
    // settings because active button color settings does not have mode.
    return mode === undefined;
  }, [mode]);

  const modeOptions = [
    { label: t('lucent_theme.new_static_color'), value: ModeOption.NEW },
    { label: t('lucent_theme.existing_static_color'), value: ModeOption.EXISTING },
    { label: t('lucent_theme.flame'), value: ModeOption.FLAME },
    { label: t('lucent_theme.ambient'), 'value': ModeOption.AMBIENT }
  ]

  const handleHasColorChange = useCallback(
    () => setHasColor(p => !p), [setHasColor]
  );

  const handleSelectedDeviceChange = (
    selectedOptions: Array<SelectItem<string>>,
  ) => {
    if (selectedOptions.some(option => option.value === "remove_all")) {
      setSelectedDeviceList([]); return;
    }
    if (selectedOptions.some(option => option.value === "select_all")) {
      setSelectedDeviceList(deviceOptionList.filter(
        option => option.value !== "select_all" &&
                  option.value !== "remove_all")); return;
    }
    setSelectedDeviceList(selectedOptions);
  }

  const handleSendToDevicesClick = useCallback(() => {
    const method = isActiveButton ? 'active' : 'primary';
    if (!selectedDeviceList?.length) {
      failure(t('lucent_theme.no_devices_selected')); 
      return;
    }
    const isAllDevicesSelected = (deviceList: Array<SelectItem<string>>, optionList: Array<SelectItem<string>>) => {
      // We're determining if deviceList has all elements in optionList,
      // except for the 'select_all' and 'remove_all' options. So, we're
      // subtracting 2 from the length of optionList.
      return deviceList.length === optionList.length - 2;
    }

    const bridgeDeviceList = isAllDevicesSelected(selectedDeviceList, deviceOptionList) ? ['all'] : selectedDeviceList.map(device => device.value);
    console.log(bridgeDeviceList);
    rpc.setLucentButtonColor(
      method,
      mode,
      color,
      bridgeDeviceList
    ).then(() => {
      if (isActiveButton) {
        success(t('lucent_theme.lucent_color_settings_for_active_button_sent_to_devices'));
      } else {
        success(t('lucent_theme.lucent_color_settings_for_primary_button_sent_to_devices'));
      }
    });
  }, [rpc, mode, color, selectedDeviceList, success, t, isActiveButton,
      failure, deviceOptionList]);

  
  return (
    <>
      <h2>{title}</h2>
      <SettingCheckBoxComponent
        label={t('lucent_theme.has_color')}
        set={handleHasColorChange}
        value={hasColor}
      />
      {(hasColor && !isActiveButton) && 
        <SettingSelectComponent
          label={t("mode")}
          options={modeOptions}
          value={modeOptions.find(option => option.value === mode)}
          onChange={(option: { label: string, value: string }) => setMode(option.value)}
        />
      }
      {
        // If setMode is null, then we are showing active button color 
        // settings because active button color settings does not have mode.
        // (ModeOption.NEW || (isActiveButton && hasColor) || mode === ModeOption.NEW) && <>
        ((isActiveButton && hasColor) || (hasColor && mode === ModeOption.NEW)) && <>
          <SettingColorInputComponent
            label={t('lucent_theme.button_color')}
            color={color}
            onChange={setColor}
          />
        </>
      }
      {hasColor && 
        <>
        <SettingSelectComponent
          label={`${t("devices")} (${selectedDeviceList.length})`}
          options={deviceOptionList}
          value={selectedDeviceList}
          isMulti={true}
          onChange={handleSelectedDeviceChange}
        />
      <div className="line">
        <div className="left"></div>
        <div className="right">
          <button
                className="button button-green"
                onClick={handleSendToDevicesClick}
              >{t('lucent_theme.send_to_devices')}</button>
            </div>
          </div>
        </>
      }
    </>
  )
})

export const LucentThemeEditScreen = memo(() => {
  const { t } = useLocalization();
  const [rpc] = useRPC();
  const { success, failure } = useMessage();
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const question = useQuestion();

  const lucentRGBSettingsList = useSelector(
    state => state.control.lucentRGBSettingsList
  );

  const selected = useMemo(() => lucentRGBSettingsList.find(
    lucentRGBSettings => lucentRGBSettings.id === params?.id
  ) || null, [lucentRGBSettingsList, params?.id]
  );

  const [themeName, setThemeName] = useState<string>(
    selected ? selected.name : ''
  );
  const [setActiveInput, renderKeyboard, currentInput] = useKeyboard(); 

  const primaryButtonSupportedDevicesFilter = useCallback((device: BridgeDeviceModel) => {
    return SUPPORTED_DEVICE_IDS.includes(device.type_id);
  }, []);

  const activeButtonSupportedDevicesFilter = useCallback((device: BridgeDeviceModel) => {
    return ACTIVE_BUTTON_SUPPORTED_DEVICE_IDS.includes(device.type_id);
  }, []);

  const primaryButtonDeviceOptionList = [
    { label: t("select_all"), value: "select_all" },
    { label: t("remove_all"), value: "remove_all" }
  ].concat(useMapSelector(
    state => state.control.bridgeDeviceList.filter(primaryButtonSupportedDevicesFilter),
    device => ({ label: device.name, value: device.id }),
  ) || []);

  const activeButtonDeviceOptionList = [
    { label: t("select_all"), value: "select_all" },
    { label: t("remove_all"), value: "remove_all" }
  ].concat(useMapSelector(
    state => state.control.bridgeDeviceList.filter(activeButtonSupportedDevicesFilter),
    device => ({ label: device.name, value: device.id }),
  ) || []);

  // Active Button Properties ------------------------------------------------

  const [hasPrimaryButtonColor, setHasPrimaryButtonColor] = useState<boolean>(
    selected ? selected.primary_button_color.is_enabled : false
  );

  const [primaryButtonColor, setPrimaryButtonColor] = useState<RgbColor>(
    selected ? selected.primary_button_color.color : { r: 0, g: 0, b: 0 }
  );

  const [primaryButtonMode, setPrimaryButtonMode] = useState<ModeOption>(
    selected ? selected.primary_button_color.mode as ModeOption : ModeOption.NONE
  );

  const [
    selectedPrimaryButtonDeviceList, setSelectedPrimaryButtonDeviceList
  ] = useState<Array<SelectItem<string>>>(
    selected ?
      selected.primary_button_color.bridge_device_list.every(device => device === 'all') ?
        primaryButtonDeviceOptionList.filter(option => option.value !== 'select_all' && option.value !== 'remove_all') :    
        selected.primary_button_color.bridge_device_list.map(
          device => primaryButtonDeviceOptionList.find(option => option.value === device)
        ) : []
  );

  // Active Button Properties ------------------------------------------------

  const [hasActiveButtonColor, setHasActiveButtonColor] = useState<boolean>(
    selected ? selected.active_button_color.is_enabled : false
  );

  const [activeButtonColor, setActiveButtonColor] = useState<RgbColor>(
    selected ? selected.active_button_color.color : { r: 0, g: 0, b: 0 }
  );

  const [selectedActiveButtonDeviceList, setSelectedActiveButtonDeviceList] = useState<Array<SelectItem<string>>>(
    selected ?
      selected.active_button_color.bridge_device_list.every(device => device === 'all') ?
        activeButtonDeviceOptionList.filter(option => option.value !== 'select_all' && option.value !== 'remove_all') :
        selected.active_button_color.bridge_device_list.map(
          device => activeButtonDeviceOptionList.find(
            option => option.value === device
          )
        ) : []
  );

  const onSave = useCallback(() => {
    if (themeName === '') {
      failure(t('lucent_theme.lucent_theme_name_required'));
      return;
    }
    if (!hasPrimaryButtonColor && !hasActiveButtonColor) {
      failure(t('lucent_theme.at_least_one_color_required'));
      return;
    }
    if (hasPrimaryButtonColor && primaryButtonMode === ModeOption.NONE) {
      failure(t('lucent_theme.primary_button_mode_required'));
      return;
    }
    if (hasPrimaryButtonColor && selectedPrimaryButtonDeviceList.length === 0) {
      failure(t('lucent_theme.primary_button_device_required'));
      return;
    }
    if (hasActiveButtonColor && selectedActiveButtonDeviceList.length === 0) {
      failure(t('lucent_theme.active_button_device_required'));
      return;
    }
    const isAllDevicesSelected = (deviceList: Array<SelectItem<string>>, optionList: Array<SelectItem<string>>) => {
      // We're determining if deviceList has all elements in optionList,
      // except for the 'select_all' and 'remove_all' options. So, we're
      // subtracting 2 from the length of optionList.
      return deviceList.length === optionList.length - 2;
    }
    const primaryButtonParams = {
      is_enabled: hasPrimaryButtonColor,
      mode: primaryButtonMode,
      color: primaryButtonColor,
      bridge_device_list: isAllDevicesSelected(
        selectedPrimaryButtonDeviceList,
        primaryButtonDeviceOptionList
      ) ? ['all'] : selectedPrimaryButtonDeviceList.map(
        device => device.value
      )
    }
    const activeButtonParams = {
      is_enabled: hasActiveButtonColor,
      color: activeButtonColor,
      bridge_device_list: isAllDevicesSelected(
        selectedActiveButtonDeviceList,
        activeButtonDeviceOptionList
      ) ? ['all'] : selectedActiveButtonDeviceList.map(
        device => device.value
      )
    }
    // console.log(primaryButtonParams);
    // console.log(activeButtonParams);
    rpc.updateLucentRGBSettings(
      selected?.id, themeName, primaryButtonParams, activeButtonParams,
    ).then(() => {
      success(t('lucent_theme.lucent_color_update_success'));
      history.push(BACK_URL)
    }).catch((reason) => {
      failure(reason);
    });
  }, [
    rpc, themeName, hasPrimaryButtonColor, primaryButtonColor,
    primaryButtonMode, selectedPrimaryButtonDeviceList,
    hasActiveButtonColor, activeButtonColor,
    selectedActiveButtonDeviceList, success, failure, t, selected, history,
    primaryButtonDeviceOptionList, activeButtonDeviceOptionList
  ]);

  const onDelete = useCallback(() => {
    question(t('lucent_theme.are_you_sure_you_want_to_delete'), () => {
      rpc.deleteLucentRGBSettings(selected.id).then(() => {
        success(t("lucent_theme.lucent_color_delete_success"))
        history.push(BACK_URL)
      }).catch(failure)
    });
  }, [rpc, selected, success, failure, history, question, t]);

  const buttons = useMemo(
    () => {
      let result = [{ text: t("save"), onClick: onSave, className: "green", }]
      if (selected) {
        result.push({ text: t("delete"), onClick: onDelete, className: "red" })
      }
      return result;
    }, [t, onSave, selected, onDelete])

    const onBlur = useCallback(() => {
            setActiveInput(null)
        }, [setActiveInput])

  return (
    <PasswordContainer requiredRole="default" isFullscreen={true}>
      <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings/custom-menu/lucent-themes/"} currentInput={currentInput}>
        <div className="content-divider">
          <div className="content-wrapper">
          <SettingInputComponent
              placeholder={t("lucent_theme.write_lucent_theme_name")}
              label={t("lucent_theme.lucent_theme_name")}
              value={themeName}
              setValue={setThemeName}
              setActiveInput={setActiveInput}
              onBlur={onBlur}
            />
            <ButtonSettings
              title={t('lucent_theme.primary_button_color_settings')}
              hasColor={hasPrimaryButtonColor}
              setHasColor={setHasPrimaryButtonColor}
              mode={primaryButtonMode}
              setMode={setPrimaryButtonMode}
              color={primaryButtonColor}
              setColor={setPrimaryButtonColor}
              selectedDeviceList={selectedPrimaryButtonDeviceList}
              setSelectedDeviceList={setSelectedPrimaryButtonDeviceList}
              deviceOptionList={primaryButtonDeviceOptionList}
            />
          </div>
          <div className="content-wrapper">
            <div className="line"></div>
            <ButtonSettings
              title={t('lucent_theme.active_button_color_settings')}
              hasColor={hasActiveButtonColor}
              setHasColor={setHasActiveButtonColor}
              color={activeButtonColor}
              setColor={setActiveButtonColor}
              selectedDeviceList={selectedActiveButtonDeviceList}
              setSelectedDeviceList={setSelectedActiveButtonDeviceList}
              deviceOptionList={activeButtonDeviceOptionList}
            />
          </div>
        </div>
      </SettingsPageContainer >
      {
          renderKeyboard()
      }
    </PasswordContainer >
  )
})